import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import FirearmBarrelMakeTable from '../../../components/tables/FirearmBarrelMakeTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useFirearmBarrelMakeSearch, useDeleteFirearmBarrelMake } from '../../../hooks/useFirearmBarrelMakeApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { FirearmBarrelMakeDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const FirearmBarrelMakeList: React.FC = () => {
  const { search } = useFirearmBarrelMakeSearch();
  const deleteFirearmBarrelMake = useDeleteFirearmBarrelMake();
  const [items, setItems] = useState<FirearmBarrelMakeDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Firearm Barrel Makes', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    setIsLoading(true);
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response.results);
      setTotalRecords(response.totalCount);
    } catch (error) {
      console.error('Error fetching FirearmBarrelMakes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: FirearmBarrelMakeDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteFirearmBarrelMake.mutateAsync(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting FirearmBarrelMake:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/firearmbarrelmakes"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.FirearmBarrelMake.View"
    >
      <Card
        title="Manage Firearm Barrel Makes"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/firearmbarrelmakes/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Enter Title"
            />
          </div>
          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <FirearmBarrelMakeTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/firearmbarrelmakes/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default FirearmBarrelMakeList;
